/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Clipboard} from '@angular/cdk/clipboard';
import {Injectable} from '@angular/core';
import {FabMessageService} from '@fabric/components/message';
import {CellClassParams, ColDef, GridApi} from 'ag-grid-community';
import {DISMISS_MSG} from '../../common/constants';
import {between, getProperty} from './generic-helpers';


@Injectable()
export class CopyPaste {
  selectedIndices: { column: string, rowIndex: number }[] = [];
  isSelecting: boolean = false;
  hoveredCell: { column: string, rowIndex: number } = null;

  constructor(public snackBar: FabMessageService,
              private readonly clipboard: Clipboard) {
  }

  /**
   * gets the mouse down event on any of the cells
   * @param event the generated event
   */
  cellMouseDown(event) {
    this.selectedIndices = [];
    this.isSelecting = true;
    this.selectedIndices.push({rowIndex: event.rowIndex, column: event.column.colId});
  }

  /**
   * Gets the mouse released event and turns isSelecting off
   */
  mouseReleased(gridApi) {
    if (this.isSelecting) {
      this.isSelecting = false;
    }
    gridApi.redrawRows();
  }

  /**
   * Grabs the hovered cell and highlights it if isSelecting is true
   * @param event the generated event
   * @param gridApi The grid api for AG grid
   */
  cellHover(event, gridApi) {
    this.hoveredCell = {rowIndex: event.rowIndex, column: event.column.colId}
    if (this.isSelecting) {
      this.selectedIndices[1] = this.hoveredCell;
      gridApi.redrawRows();
    }
  }

  /**
   * gets if a cell is selected or not
   * @param params Cell class parameters generated by AG grid
   * @param columnDef The column definition of the table
   */
  isCellSelected(params: CellClassParams, columnDef: ColDef[]) {
    if (this.selectedIndices.length < 1) {
      return false;
    }
    if (this.selectedIndices.length == 1) {
      this.selectedIndices.push(this.selectedIndices[0]);
    }
    const columns = columnDef.map(column => column.field);
    const startCoords = columns.indexOf(this.selectedIndices[0].column);
    const endCoords = columns.indexOf(this.selectedIndices[1].column);
    const currentIndex = columns.indexOf(params.column.getColId());

    return between(currentIndex, startCoords, endCoords, true) &&
      between(params.node.rowIndex, this.selectedIndices[0].rowIndex, this.selectedIndices[1].rowIndex, true);
  }


  /**
   * Handles the keyboard event for pasting
   * @param event the keydown event generated by the browser
   * @param columnDef The column definition of the table
   * @param gridApi The grid api for AG grid
   * @param pageSize the size of the page
   */
  async keyboardEvent(event, columnDef: ColDef[], gridApi: GridApi, pageSize) {
    if ((event.ctrlKey || event.metaKey) && event.code == "KeyV") {
      if (!navigator.clipboard) {
        this.snackBar.displayError('Clipboard API not supported', DISMISS_MSG);
        return;
      }
      await this.paste(columnDef, pageSize, gridApi);
    } else if ((event.ctrlKey || event.metaKey) && event.code == "KeyC") {
      this.copy(columnDef, gridApi);
    }
  }

  /**
   * Copies the selected cells to the clipboard
   * @param columnDef The column definition of the table
   * @param gridApi The grid api for AG grid
   */
  copy(columnDef: ColDef[], gridApi: GridApi) {
    try {
      const columns = columnDef.map(element => element.field);
      const columnIndices = [columns.indexOf(this.selectedIndices[0].column),
        columns.indexOf(this.selectedIndices[1].column)];
      const startX = columnIndices[0] <= columnIndices[1] ? columnIndices[0] : columnIndices[1];
      const startY = this.selectedIndices[0].rowIndex <= this.selectedIndices[1].rowIndex ? this.selectedIndices[0].rowIndex : this.selectedIndices[1].rowIndex;
      const endX = columnIndices[0] > columnIndices[1] ? columnIndices[0] : columnIndices[1];
      const endY = this.selectedIndices[0].rowIndex > this.selectedIndices[1].rowIndex ? this.selectedIndices[0].rowIndex : this.selectedIndices[1].rowIndex;
      let copyString = '';
      for (let i = startY; i <= endY; i++) {
        const rowNode = gridApi.getRowNode(String(i));
        const rowValues = [];
        for (let j = startX; j <= endX; j++) {
          let cellValue: any = getProperty(rowNode.data, columns[j]);
          if (!cellValue) {
            cellValue = "";
          }
          rowValues.push(cellValue);
        }
        copyString += rowValues.join("\t") + '\n';
      }
      this.clipboard.copy(copyString);
    } catch {
      this.snackBar.displayError('Failed to copy to clipboard', DISMISS_MSG);
    }
  }

  /**
   * Handles the paste event
   * @param event the paste event generated by the browser
   * @param columnDef The column definition of the table
   * @param gridApi The grid api for AG grid
   * @param pageSize the size of the page
   */
  async pasteEvent(event, columnDef: ColDef[], gridApi, pageSize) {
    event.preventDefault();
    await this.paste(columnDef, pageSize, gridApi);
  }

  /**
   * Pastes the data into the grid
   * @param columnDef The column definition of the table
   * @param pageSize The size of the current visible page
   * @param gridApi The grid api for AG grid
   */
  async paste(columnDef: ColDef[], pageSize, gridApi) {
    try {
      let text = await navigator.clipboard.readText();
      if (text.endsWith("\r\n")) {
        text = text.slice(0, -2);
      }
      const rows = text.split("\r\n");
      const data = rows.map(row => row.split("\t"));
      const columns = columnDef.map(element => element.field);

      const pasteStartColumnIndex = columns.indexOf(this.selectedIndices[0].column);
      const pasteStartRowIndex = this.selectedIndices[0].rowIndex;

      //if it's not an editable cell throw error
      if (!columnDef[pasteStartColumnIndex].editable) {
        throw (new Error("Not an editable cell"));
      }

      // find first non-editable column
      const {
        pasteHeight,
        pasteWidth
      } = this.findPasteSize(pasteStartRowIndex, pasteStartColumnIndex, pageSize, data, columnDef);

      for (let i = 0; i < pasteHeight; i++) {
        const rowNode = gridApi.getRowNode(pasteStartRowIndex + i);
        for (let j = 0; j < pasteWidth; j++) {
          const colKey = pasteStartColumnIndex + j;
          let insertValue: any = data[i][j];
          if (columnDef[colKey].cellEditor === "agNumberCellEditor") {
            insertValue = this.formatAsNumber(insertValue);
          }
          rowNode.setDataValue(columns[colKey], insertValue);
        }
      }
    } catch {
      this.snackBar.displayError('Failed to read clipboard', DISMISS_MSG);
    }
  }

  /**
   * Finds the height and width of the paste action
   * @param pasteStartRowIndex The starting row index of the paste
   * @param pasteStartColumnIndex The starting column index of the paste
   * @param pageSize The max size of the current page
   * @param data The data to be pasted as a 2d Array
   * @param columnDef The AG Grid column definition
   * @private returns the height and width of the paste
   */
  private findPasteSize(pasteStartRowIndex: number, pasteStartColumnIndex: number, pageSize: number, data: string[][],
                        columnDef: ColDef[]) {
    let firstNonEditableColumnIndex = null;
    for (let colIndex = pasteStartColumnIndex; colIndex < columnDef.length; colIndex++) {
      if (colIndex - pasteStartColumnIndex > data[0].length) {
        break;
      }
      if (!columnDef[colIndex].editable) {
        firstNonEditableColumnIndex = colIndex;
      }
    }

    const maxRowSize = pageSize;
    const maxColSize = firstNonEditableColumnIndex ? Math.min(columnDef.length, firstNonEditableColumnIndex) : columnDef.length;

    const pasteHeight = Math.min(maxRowSize - pasteStartRowIndex, data.length);
    const pasteWidth = Math.min(maxColSize - pasteStartColumnIndex, data[0].length);
    return {pasteHeight, pasteWidth};
  }

  /**
   * formats a formatted number as a raw number
   * @param value the input number string
   * @returns the number as a number, or NaN if it's not a number
   */
  private formatAsNumber(value: string): number {
    const digits: string = '0123456789';
    const formattedValue = value.replace(/,/g, '').trim();
    for (const character of formattedValue) {
      if (!digits.includes(character)) {
        return NaN;
      }
    }
    return Number(formattedValue);
  }
}
